import { GOLDEN_ANGLE_DEG, PRESETS, clamp } from "./phyllotaxis";
import { exportPng, paintHead } from "./draw";
import {
  DEFAULT_STATE,
  readingFor,
  roundDeg,
  sanitize,
  toHash,
  type AppState,
  type ColorMode,
  parseHash,
} from "./state";

const canvas = document.querySelector<HTMLCanvasElement>("#head")!;
const surface = canvas.getContext("2d");
if (!surface) throw new Error("Canvas is unavailable");
const ctx: CanvasRenderingContext2D = surface;

const degRange = document.querySelector<HTMLInputElement>("#deg")!;
const degNum = document.querySelector<HTMLInputElement>("#deg-num")!;
const seedsRange = document.querySelector<HTMLInputElement>("#seeds")!;
const degReadout = document.querySelector("#deg-readout")!;
const degLabel = document.querySelector("#deg-label")!;
const degMeta = document.querySelector("#deg-meta")!;
const seedsLabel = document.querySelector("#seeds-label")!;
const readingEl = document.querySelector("#reading")!;
const live = document.querySelector("#live")!;
const overlayBtn = document.querySelector<HTMLButtonElement>("#overlay")!;
const petalsBtn = document.querySelector<HTMLButtonElement>("#petals")!;
const kernelBtn = document.querySelector<HTMLButtonElement>("#color-kernel")!;
const familyBtn = document.querySelector<HTMLButtonElement>("#color-family")!;
const presetsEl = document.querySelector("#presets")!;

let state: AppState = sanitize({
  ...DEFAULT_STATE,
  ...parseHash(window.location.hash),
});
let demoTimer = 0;
let reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
window.matchMedia("(prefers-reduced-motion: reduce)").addEventListener("change", (e) => {
  reduced = e.matches;
});

function formatDeg(deg: number): string {
  return `${deg.toFixed(3)}°`;
}

function syncHash(): void {
  const next = toHash(state);
  if (window.location.hash !== next) history.replaceState(null, "", next);
}

function renderReading(): void {
  const r = readingFor(state);
  readingEl.className = `reading ${r.kind}`;
  readingEl.innerHTML = `<h2>${r.title}</h2><p>${r.body}</p>`;
  const err = Math.abs(state.degrees - GOLDEN_ANGLE_DEG);
  degMeta.textContent =
    err < 0.002
      ? "The golden turn (360° / φ²)"
      : `${err.toFixed(3)}° from the golden turn · ${r.parastichies.a} / ${r.parastichies.b} spirals`;
  live.textContent = `${formatDeg(state.degrees)}. ${r.title}`;
}

function renderControls(): void {
  const d = roundDeg(state.degrees);
  degRange.value = String(clamp(d, 90, 180));
  degNum.value = String(d);
  seedsRange.value = String(state.seeds);
  degReadout.innerHTML = `${d.toFixed(3)}<span>°</span>`;
  degLabel.textContent = formatDeg(d);
  seedsLabel.textContent = String(state.seeds);
  overlayBtn.setAttribute("aria-pressed", String(state.overlay));
  petalsBtn.setAttribute("aria-pressed", String(state.petals));
  kernelBtn.setAttribute("aria-pressed", String(state.color === "kernel"));
  familyBtn.setAttribute("aria-pressed", String(state.color === "family"));
  for (const btn of presetsEl.querySelectorAll("button")) {
    const deg = Number(btn.dataset.deg);
    btn.setAttribute("aria-pressed", String(Math.abs(deg - d) < 0.0005));
  }
  renderReading();
}

function paint(): void {
  const stage = canvas.parentElement!;
  const cssW = Math.max(1, stage.clientWidth);
  const cssH = Math.max(1, stage.clientHeight);
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.floor(cssW * dpr);
  canvas.height = Math.floor(cssH * dpr);
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  paintHead(ctx, cssW, cssH, dpr, state);
}

function setState(partial: Partial<AppState>, paintNow = true): void {
  state = sanitize({ ...state, ...partial });
  renderControls();
  syncHash();
  if (paintNow) paint();
}

function nudge(delta: number): void {
  setState({ degrees: roundDeg(state.degrees + delta) });
}

function playDemo(): void {
  window.clearTimeout(demoTimer);
  setState({ degrees: 137.5 });
  if (reduced) {
    setState({ degrees: 137.4 });
    return;
  }
  const from = 137.5;
  const to = 137.4;
  const dur = 1100;
  demoTimer = window.setTimeout(() => {
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - (1 - t) * (1 - t);
      setState({ degrees: roundDeg(from + (to - from) * eased) });
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, 700);
}

for (const p of PRESETS) {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = p.label;
  b.dataset.deg = String(p.deg);
  b.title = p.hint;
  b.addEventListener("click", () => setState({ degrees: p.deg }));
  presetsEl.append(b);
}

degRange.addEventListener("input", () => setState({ degrees: Number(degRange.value) }));
degNum.addEventListener("change", () => setState({ degrees: Number(degNum.value) }));
seedsRange.addEventListener("input", () => setState({ seeds: Number(seedsRange.value) }));
document.querySelector("#nudge-down")!.addEventListener("click", () => nudge(-0.1));
document.querySelector("#nudge-up")!.addEventListener("click", () => nudge(0.1));
document.querySelector("#demo")!.addEventListener("click", playDemo);
overlayBtn.addEventListener("click", () => setState({ overlay: !state.overlay }));
petalsBtn.addEventListener("click", () => setState({ petals: !state.petals }));
kernelBtn.addEventListener("click", () => setState({ color: "kernel" satisfies ColorMode }));
familyBtn.addEventListener("click", () => setState({ color: "family" satisfies ColorMode }));
document.querySelector("#save")!.addEventListener("click", () => exportPng(canvas));
document.querySelector("#reset")!.addEventListener("click", () => setState({ degrees: GOLDEN_ANGLE_DEG }));

window.addEventListener("hashchange", () => {
  setState(parseHash(window.location.hash));
});

window.addEventListener("keydown", (e) => {
  const t = e.target;
  if (t instanceof HTMLInputElement && (t.type === "number" || t.type === "text")) return;
  if (e.key === "ArrowLeft") {
    e.preventDefault();
    nudge(e.shiftKey ? -0.01 : -0.1);
  } else if (e.key === "ArrowRight") {
    e.preventDefault();
    nudge(e.shiftKey ? 0.01 : 0.1);
  }
});

const ro = new ResizeObserver(() => paint());
ro.observe(canvas.parentElement!);

renderControls();
paint();
